type CachedAccessToken = {
  readonly token: string | null;
  readonly orgId: string | undefined;
  readonly expiresAtMs: number;
};

let cachedAccessToken: CachedAccessToken | null = null;
const inFlightAccessTokens = new Map<string, Promise<string | null>>();

const TOKEN_REUSE_BUFFER_MS = 30_000;

const toAccessTokenCacheKey = (orgId: string | undefined): string =>
  orgId ?? "";

export const readCachedAccessToken = (
  orgId?: string,
): string | null | undefined => {
  if (!cachedAccessToken) {
    return undefined;
  }
  if (cachedAccessToken.orgId !== orgId) {
    return undefined;
  }
  if (cachedAccessToken.expiresAtMs <= Date.now() + TOKEN_REUSE_BUFFER_MS) {
    return undefined;
  }
  return cachedAccessToken.token;
};

export const writeCachedAccessToken = (
  token: string | null,
  orgId: string | undefined,
  expiresInSeconds: number,
): void => {
  cachedAccessToken = {
    token,
    orgId,
    expiresAtMs: Date.now() + Math.max(60, expiresInSeconds) * 1000,
  };
};

export const clearCachedAccessToken = (): void => {
  cachedAccessToken = null;
  inFlightAccessTokens.clear();
};

export const runDedupedAccessTokenFetch = (
  orgId: string | undefined,
  fetchToken: () => Promise<{ accessToken: string | null; expiresIn: number }>,
): Promise<string | null> => {
  const cacheKey = toAccessTokenCacheKey(orgId);
  const cached = readCachedAccessToken(orgId);
  if (cached !== undefined) {
    return Promise.resolve(cached);
  }
  const existingInFlight = inFlightAccessTokens.get(cacheKey);
  if (existingInFlight) {
    return existingInFlight;
  }
  const inFlight = (async (): Promise<string | null> => {
    try {
      const result = await fetchToken();
      writeCachedAccessToken(
        result.accessToken,
        orgId,
        result.expiresIn,
      );
      return result.accessToken;
    } finally {
      inFlightAccessTokens.delete(cacheKey);
    }
  })();
  inFlightAccessTokens.set(cacheKey, inFlight);
  return inFlight;
};
